import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { deletedArticlesSlice } from "./deletedArticlesSlice"
import { cloneDeep } from "lodash"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

export const fetchArticles = createAsyncThunk(
	"notificationPanelArticles/fetchArticles",
	async (params) => {
		const { workspaceId, sharedArticleArray, enqueueSnackbar, sharedWorkspaceId } = params
		const response = await axios.get(`/api/workspace/${workspaceId}/article?search=${sharedArticleArray.join(",")}&shared=1`)
		const { count, articles } = response.data
		if (count === -1) {
			enqueueSnackbar("This search returned no results, check your query and try again", {
				variant: "error",
				autoHideDuration: 3000,
				disableWindowBlurListener: true,
				preventDuplicate: true,
			})
			return { articles: [], count, sharedWorkspaceId, workspaceId }
		} else {
			return { articles, count, sharedWorkspaceId, workspaceId }
		}
	}
)

const lookup = ({articleId}, listOfArticles) => {
	const index = listOfArticles.findIndex(
		(a) => (a.articleId === articleId)
	) 
	return index
}

const initialState = {
	selectedData: [],
	isLoading: false,
	articles: [],
	count: 0
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

export const notificationPanelArticlesSlice = createSlice({
	name: "notificationPanelArticles",
	initialState: {
		byWorkspace: {}
	},
	reducers: {
		clearNotificationPanelView: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.selectedData = initialState.selectedData
			workspace.isLoading = initialState.isLoading
			workspace.articles = initialState.articles
			workspace.count = initialState.count
		},
		updateArticle: (state, action) => {
			const articleWorkspace = action.payload
			const { DOI, PMID } = articleWorkspace.article
			const workspace = ensureWorkspace(state, articleWorkspace.workspaceId)
			const articleList = cloneDeep(workspace.articles)
			let index = articleList.findIndex(
				(a) => (a.DOI !== undefined && a.DOI === DOI) || (a.PMID !== undefined && a.PMID === PMID)
			)
			if (index === -1) {
				index = lookup(articleWorkspace,articleList)
			}
			if (index !== -1) {
				workspace.articles.splice(index, 1, articleWorkspace)
			} else {
				console.error("533 regular update Something went wrong when updating notification panel citations")
			}
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchArticles.pending, (state, action) => {
				const {workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = true
			})
			.addCase(fetchArticles.fulfilled, (state, action) => {
				const {articles, count, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.articles = articles
				workspace.count = count
				workspace.isLoading = false
			})
			.addCase(fetchArticles.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = false
			})
			.addCase(deletedArticlesSlice.actions.addDeletedArticle, (state, action) => {
				const articleList = cloneDeep(state.articles)
				const index = lookup(action.payload, articleList)
				const articleToUpdate = articleList[index]
				if (articleToUpdate) {
					articleToUpdate.deletedAt = action.payload.deletedAt
					articleList[index] = articleToUpdate
					state.articles = articleList
				}
			})
	},
})

export const {
	clearNotificationPanelView,
	updateArticle,
} = notificationPanelArticlesSlice.actions

// Selector to get articles
export const selectArticles = (state, workspaceId) => state.notificationPanelArticles.byWorkspace[workspaceId]?.articles ?? initialState.articles

// Selector to get the count
export const selectArticleCount = (state, workspaceId) => state.notificationPanelArticles.byWorkspace[workspaceId]?.count ?? initialState.count

// Selector to check if the data is loading
export const selectIsLoading = (state, workspaceId) => state.notificationPanelArticles.byWorkspace[workspaceId]?.isLoading ?? initialState.isLoading

export default notificationPanelArticlesSlice.reducer
